import { EncounterChargeItemRow } from "@/encounters/types/encounter-charge-item-rows";
import { StagedProceduresOperation } from "@/encounters/types/procedures";
import { buildStagedProceduresOperation } from "@/encounters/utils/staged-procedures";
import {
  ProceduresPickerFormType,
  proceduresPickerInitialValues,
} from "@/encounters/zod-schemas/procedures";

/** Marks a row the section is only previewing, so nothing takes it for a charge that exists. */
const STAGED_ROW_ID_PREFIX = "staged-biller-row-";

export const isStagedBillerSectionRow = (rowId: string): boolean =>
  rowId.startsWith(STAGED_ROW_ID_PREFIX);

/**
 * The rows a biller's staged entries will become once Save Billing Info writes them.
 *
 * Built from the picker's own values rather than by replaying the FHIR bundle the way the procedures
 * tab previews its work: a biller-section row is flat — code, description, modifiers, diagnoses,
 * units — so the session already holds everything a row needs.
 *
 * Every procedure in a session is linked to every diagnosis in it, which is what the write does, so
 * each row lists all of them.
 *
 * Keyed by code, newest session winning, because that is how the write reconciles: staging the same
 * CPT twice updates one resource and produces one charge rather than two.
 */
export function buildStagedBillerSectionRows(
  operations: StagedProceduresOperation[],
): EncounterChargeItemRow[] {
  const rowsByCode = new Map<string, EncounterChargeItemRow>();

  for (const { formValues } of operations) {
    const conditions = [
      ...new Map(
        formValues.conditions
          .filter(({ code }) => code)
          .map((condition) => [condition.code, condition]),
      ).values(),
    ];

    for (const procedure of formValues.procedures) {
      if (!procedure.code) continue;

      rowsByCode.set(procedure.code, {
        id: `${STAGED_ROW_ID_PREFIX}${procedure.code}`,
        procedureCode: procedure.code,
        codeDescription: procedure.display,
        modifiers: procedure.modifiers.filter((modifier) => Boolean(modifier?.trim())),
        diagnosisCodes: conditions.map(({ code }) => code),
        // What the row's Edit action opens on. Taken from the session rather than rebuilt, because
        // nothing has been written yet and the session is all there is.
        editTarget: { procedure, conditions },
        // Capture carries the picker's own number onto the charge, so previewing it here matches
        // what the row will read once it is saved.
        units: procedure.units,
      });
    }
  }

  return [...rowsByCode.values()];
}

/**
 * The staged sessions with one procedure code taken out of them.
 *
 * Deleting a row the section has not written yet has no charge to void, so the entry comes off the
 * staged set instead. Rows are keyed by code above — the same way the write reconciles — so dropping
 * the code is exactly the inverse of the row it produced, whichever session added it.
 *
 * A session left with no procedures is dropped whole. Its diagnoses were there to bill under, and
 * saving one on its own would put a diagnosis on the encounter that no row would ever show.
 */
export function dropStagedProcedureCode(
  operations: StagedProceduresOperation[],
  procedureCode: string,
): StagedProceduresOperation[] {
  return operations.reduce<StagedProceduresOperation[]>((remaining, operation) => {
    const procedures = operation.formValues.procedures.filter(({ code }) => code !== procedureCode);

    if (procedures.length) {
      remaining.push({ ...operation, formValues: { ...operation.formValues, procedures } });
    }

    return remaining;
  }, []);
}

/**
 * The staged sessions with one procedure code re-staged under the diagnoses the biller just chose.
 *
 * This is what editing a row that has not been written yet amounts to. There is no charge to update,
 * so the entry comes off the staged set and goes back on carrying the finished session.
 *
 * The code is dropped from wherever it was staged and added as a session of its own, which splits it
 * away from any procedure it was sharing diagnoses with. That is the point: the biller changed what
 * this one code is billed under, and the codes staged alongside it keep what they had.
 *
 * The new session starts from blank initial values, because nothing about it is on the server yet.
 * A write reconciles it by code, so it adds what is missing and touches nothing else.
 */
export function replaceStagedProcedure(
  operations: StagedProceduresOperation[],
  procedureCode: string,
  formValues: ProceduresPickerFormType,
): StagedProceduresOperation[] {
  return [
    ...dropStagedProcedureCode(operations, procedureCode),
    buildStagedProceduresOperation(formValues, proceduresPickerInitialValues),
  ];
}
